import { Suspense, lazy, useEffect, useRef, useState, type CSSProperties } from "react";

// The dotLottie player is a ~670 KB chunk. Import it dynamically AND only mount it
// once the placeholder scrolls near the viewport, so it never sits on the critical
// path of the initial page load. Decorative animations don't need to block paint.
const DotLottieReact = lazy(() =>
  import("@lottiefiles/dotlottie-react").then((m) => ({ default: m.DotLottieReact })),
);

type LazyLottieProps = {
  src: string;
  loop?: boolean;
  autoplay?: boolean;
  className?: string;
  style?: CSSProperties;
};

export function LazyLottie({ className, style, ...props }: LazyLottieProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);
  // Decide synchronously so the ~1.3 MB player + WASM chunk is never even requested
  // on phones / touch / reduced-motion, where its multi-second decode dominates the
  // mobile performance score. These animations are purely decorative.
  const [enabled] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return (
      !window.matchMedia("(max-width: 767px), (pointer: coarse)").matches &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShow(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);

  return (
    <div ref={ref} className={className ?? "h-full w-full"} style={style}>
      {enabled && show ? (
        <Suspense fallback={null}>
          <DotLottieReact src={props.src} loop={props.loop} autoplay={props.autoplay} />
        </Suspense>
      ) : null}
    </div>
  );
}
